import React from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import rehypeHighlight from "rehype-highlight";
import remarkGfm from "remark-gfm";

/**
 * 智能文本格式化组件
 * 支持自动识别表格、列表、标题等结构，并提供优雅的视觉呈现
 * 增强版：支持基本Markdown语法
 */

export interface TextFormatterProps {
  content: string;
  colors?: any;
  isMobile?: boolean;
  className?: string;
  enableMarkdown?: boolean;
}

export interface ParagraphRendererProps {
  paragraph: string;
  colors: any;
  isMobile: boolean;
  isFirst: boolean;
  enableMarkdown?: boolean;
}

// 默认颜色主题
const defaultColors = {
  primary: "#21C4B8",
  primaryDark: "#4BA67A",
  primaryLight: "#6BCFA3",
  border: "rgba(43, 189, 178, 0.2)",
  text: "#0f172a",
  textSecondary: "#475569",
  shadow: "rgba(43, 189, 178, 0.15)",
};

// 段落渲染器组件
const ParagraphRenderer: React.FC<ParagraphRendererProps> = ({
  paragraph,
  colors,
  isMobile,
  isFirst,
}) => {
  const { t } = useTranslation();
  const lines = paragraph.split("\n").filter((line) => line.trim());

  // 检测段落类型
  const detectParagraphType = (lines: string[]) => {
    if (lines.length === 0) return "empty";

    // 检测表格
    const hasTableIndicators = lines.some(
      (line) =>
        line.includes("|") ||
        line.includes("\t") ||
        (line.includes(t("home_components.chart_brand")) &&
          line.includes("优势") &&
          line.includes("推荐"))
    );

    if (hasTableIndicators && lines.length >= 3) return "table";

    // 检测列表
    const hasListIndicators = lines.some(
      (line) =>
        line.trim().match(/^(\d+\.|[-*+•])\s/) ||
        line.includes("明确核心需求") ||
        line.includes("关注核心技术")
    );

    if (hasListIndicators) return "list";

    // 检测标题
    const firstLine = lines[0].trim();
    const isHeading =
      firstLine.match(/^#{1,3}\s/) ||
      firstLine.endsWith("：") ||
      firstLine.endsWith(":") ||
      firstLine.includes("如何选择") ||
      firstLine.includes("最佳选择");

    if (isHeading && lines.length === 1) return "heading";

    return "normal";
  };

  const paragraphType = detectParagraphType(lines);

  switch (paragraphType) {
    case "heading":
      return (
        <div
          style={{
            marginBottom: 20,
            padding: isMobile ? "12px 16px" : "16px 20px",
            background: `linear-gradient(135deg, ${colors.primary}08, ${colors.primaryLight}08)`,
            border: `1px solid ${colors.border}`,
            borderRadius: 6,
            borderLeft: `4px solid ${colors.primary}`,
          }}
        >
          <div
            style={{
              color: colors.primary,
              fontWeight: 700,
              fontSize: isMobile ? 16 : 18,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            💡 {lines[0].trim().replace(/^#{1,3}\s/, "")}
          </div>
        </div>
      );

    case "table":
      return renderAdvancedTable(lines, colors, isMobile, t);

    case "list":
      return renderAdvancedList(lines, colors, isMobile);

    case "normal":
    default:
      return renderNormalParagraph(lines, colors, isMobile, isFirst);
  }
};

// 高级表格渲染
const renderAdvancedTable = (
  lines: string[],
  colors: any,
  isMobile: boolean,
  t: any
) => {
  // 提取表格数据
  const tableData = lines
    .map((line) => {
      // 多种分隔符支持
      const cells = line
        .split(/\||\t|：|:/)
        .map((cell) => cell.trim())
        .filter((cell) => cell);
      return cells;
    })
    .filter((row) => row.length > 0);

  if (tableData.length === 0) return null;

  const isHeaderRow = (row: string[]) => {
    return row.some(
      (cell) =>
        cell.includes(t("home_components.chart_brand")) ||
        cell.includes("优势") ||
        cell.includes("推荐") ||
        cell.includes("适合")
    );
  };

  return (
    <div
      style={{
        background: "rgba(255, 255, 255, 0.7)",
        border: `1px solid ${colors.border}`,
        borderRadius: 6,
        padding: isMobile ? 12 : 16,
        marginBottom: 20,
        overflow: "auto",
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: isMobile ? 12 : 13,
          minWidth: isMobile ? 500 : "auto",
        }}
      >
        <tbody>
          {tableData.map((row, rowIndex) => (
            <tr
              key={rowIndex}
              style={{
                background: isHeaderRow(row)
                  ? `linear-gradient(135deg, ${colors.primary}15, ${colors.primaryLight}15)`
                  : "transparent",
                borderBottom:
                  rowIndex < tableData.length - 1
                    ? `1px solid ${colors.border}30`
                    : "none",
              }}
            >
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  style={{
                    padding: isMobile ? "10px 8px" : "12px 16px",
                    fontWeight: isHeaderRow(row) ? 700 : 400,
                    color: isHeaderRow(row) ? colors.primary : colors.text,
                    borderRight:
                      cellIndex < row.length - 1
                        ? `1px solid ${colors.border}20`
                        : "none",
                    verticalAlign: "top",
                    lineHeight: 1.5,
                  }}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// 高级列表渲染
const renderAdvancedList = (
  lines: string[],
  colors: any,
  isMobile: boolean
) => {
  return (
    <div
      style={{
        background: "rgba(255, 255, 255, 0.6)",
        border: `1px solid ${colors.border}`,
        borderRadius: 6,
        padding: isMobile ? 16 : 20,
        marginBottom: 20,
      }}
    >
      {lines.map((line, index) => {
        const trimmed = line.trim();
        const isListItem =
          trimmed.match(/^(\d+\.|[-*+•])\s/) ||
          trimmed.includes("明确核心需求") ||
          trimmed.includes("关注核心技术");

        if (!isListItem) {
          return (
            <div
              key={index}
              style={{
                marginBottom: 12,
                color: colors.text,
                lineHeight: 1.6,
              }}
            >
              {trimmed}
            </div>
          );
        }

        const content = trimmed.replace(/^(\d+\.|[-*+•])\s/, "");
        const isBold =
          content.includes("明确核心需求") || content.includes("关注核心技术");

        return (
          <div
            key={index}
            style={{
              display: "flex",
              alignItems: "flex-start",
              marginBottom: 12,
              padding: "8px 12px",
              background: "rgba(255, 255, 255, 0.8)",
              borderRadius: 6,
              border: `1px solid ${colors.border}30`,
            }}
          >
            <span
              style={{
                marginRight: 12,
                color: colors.primary,
                fontWeight: 700,
                minWidth: 24,
                fontSize: 14,
              }}
            >
              {trimmed.match(/^\d+\./) ? `${index + 1}.` : "•"}
            </span>
            <span
              style={{
                color: colors.text,
                flex: 1,
                fontWeight: isBold ? 600 : 400,
                lineHeight: 1.6,
              }}
            >
              {content}
            </span>
          </div>
        );
      })}
    </div>
  );
};

// 普通段落渲染
const renderNormalParagraph = (
  lines: string[],
  colors: any,
  isMobile: boolean,
  isFirst: boolean
) => {
  return (
    <div
      style={{
        marginBottom: 20,
        padding: isMobile ? "12px 16px" : "16px 20px",
        background: isFirst
          ? `linear-gradient(135deg, ${colors.primary}05, ${colors.primaryLight}05)`
          : "transparent",
        border: isFirst ? `1px solid ${colors.border}` : "none",
        borderRadius: isFirst ? 6 : 0,
        borderLeft: isFirst ? `4px solid ${colors.primary}30` : "none",
      }}
    >
      {lines.map((line, index) => (
        <div
          key={index}
          style={{
            marginBottom: index === lines.length - 1 ? 0 : 8,
            color: colors.text,
            lineHeight: 1.7,
            textAlign: "justify",
          }}
        >
          {line.trim()}
        </div>
      ))}
    </div>
  );
};

// 主组件
export const TextFormatter: React.FC<TextFormatterProps> = ({
  content,
  colors = defaultColors,
  isMobile = false,
  className = "",
  enableMarkdown = true,
}) => {
  const { t } = useTranslation();
  // 高级文本解析函数
  const parseContent = (text: string) => {
    if (!text || text === "正在加载AI回答...") {
      return (
        <div
          style={{
            color: colors.textSecondary,
            textAlign: "center",
            padding: 40,
            fontStyle: "italic",
          }}
        >
          {text}
        </div>
      );
    }

    // 优先走 Markdown 渲染
    if (enableMarkdown) {
      return (
        <div style={{ lineHeight: 1.8 }}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={{
              h1: ({ node, ...props }) => (
                <h1
                  style={{
                    fontSize: isMobile ? 20 : 24,
                    margin: "16px 0",
                    color: colors.text,
                  }}
                  {...props}
                />
              ),
              h2: ({ node, ...props }) => (
                <h2
                  style={{
                    fontSize: isMobile ? 18 : 20,
                    margin: "14px 0",
                    color: colors.text,
                  }}
                  {...props}
                />
              ),
              h3: ({ node, ...props }) => (
                <h3
                  style={{
                    fontSize: isMobile ? 16 : 18,
                    margin: "12px 0",
                    color: colors.text,
                  }}
                  {...props}
                />
              ),
              p: ({ node, ...props }) => (
                <p
                  style={{
                    margin: "8px 0",
                    color: colors.text,
                    textAlign: "justify" as const,
                  }}
                  {...props}
                />
              ),
              ul: ({ node, ...props }) => (
                <ul style={{ paddingLeft: 20, margin: "8px 0" }} {...props} />
              ),
              ol: ({ node, ...props }) => (
                <ol style={{ paddingLeft: 20, margin: "8px 0" }} {...props} />
              ),
              li: ({ node, ...props }) => (
                <li style={{ margin: "6px 0" }} {...props} />
              ),
              table: ({ node, ...props }) => (
                <div style={{ overflowX: "auto", margin: "12px 0" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: isMobile ? 12 : 13,
                    }}
                    {...props}
                  />
                </div>
              ),
              th: ({ node, ...props }) => (
                <th
                  style={{
                    textAlign: "left",
                    padding: "8px 12px",
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                  {...props}
                />
              ),
              td: ({ node, ...props }) => (
                <td
                  style={{
                    padding: "8px 12px",
                    borderBottom: `1px solid ${colors.border}30`,
                  }}
                  {...props}
                />
              ),
              code: ({ inline, className, children, ...props }: any) =>
                inline ? (
                  <code
                    style={{
                      background: "#f8fafc",
                      padding: "2px 6px",
                      borderRadius: 4,
                    }}
                    {...props}
                  >
                    {children}
                  </code>
                ) : (
                  <pre
                    style={{
                      background: "#0f172a",
                      color: "#e5e7eb",
                      padding: 12,
                      borderRadius: 6,
                      overflowX: "auto",
                    }}
                  >
                    <code className={className} {...props}>
                      {children}
                    </code>
                  </pre>
                ),
            }}
          >
            {text}
          </ReactMarkdown>
        </div>
      );
    }

    // 按空行分割段落（非 Markdown 路径）
    const paragraphs = text.split(/\n\s*\n/).filter((p) => p.trim());

    return (
      <div style={{ lineHeight: 1.7 }}>
        {paragraphs.map((paragraph, index) => (
          <ParagraphRenderer
            key={index}
            paragraph={paragraph}
            colors={colors}
            isMobile={isMobile}
            isFirst={index === 0}
          />
        ))}
      </div>
    );
  };

  return (
    <div
      className={className}
      style={{
        color: colors.text,
        fontSize: isMobile ? 14 : 15,
        lineHeight: 1.7,
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif',
        minHeight: 200,
      }}
    >
      {parseContent(content)}
    </div>
  );
};

// 导出为默认组件
export default TextFormatter;
